import { useTranslation } from "react-i18next";
import type { Entitlement, License } from "@/lib/types";

type Capacity = {
  activeActivations: number;
  activeProductKeys?: number;
};

function displayEntitlementValue(entitlement: Entitlement, allowed: string, denied: string) {
  if (entitlement.type === "boolean") return entitlement.value === true ? allowed : denied;
  if (entitlement.type === "list" && Array.isArray(entitlement.value)) return entitlement.value.join(", ");
  if (typeof entitlement.value === "string" || typeof entitlement.value === "number") return String(entitlement.value);
  return JSON.stringify(entitlement.value);
}

export function LicenseBusinessSummary({ license, capacity }: { license: License; capacity: Capacity }) {
  const { t } = useTranslation();
  const remainingDevices = Math.max(0, license.policy.max_devices - capacity.activeActivations);
  const entitlements = Object.values(license.entitlements ?? {}).sort((left, right) => left.key.localeCompare(right.key));

  return (
    <section className="border-t border-line pt-4" aria-labelledby="license-access-heading">
      <h3 id="license-access-heading" className="text-sm font-semibold text-ink">
        {t("uxOperations.access", "Access and capacity")}
      </h3>
      <dl className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
            {t("uxOperations.deviceCapacity", "Device capacity")}
          </dt>
          <dd className="mt-1 text-xs leading-5 text-ink">
            {t("uxOperations.deviceCapacityValue", "{{active}} active of {{limit}}; {{remaining}} available", {
              active: capacity.activeActivations,
              limit: license.policy.max_devices,
              remaining: remainingDevices,
            })}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
            {t("uxOperations.productKeyCapacity", "Active Product Keys")}
          </dt>
          <dd className="mt-1 text-xs leading-5 text-ink">
            {capacity.activeProductKeys === undefined
              ? t("uxOperations.countUnavailable", "Count unavailable. Open Seats to check or retry.")
              : t("uxOperations.productKeyCount", "{{count}} active credentials", { count: capacity.activeProductKeys })}
          </dd>
        </div>
      </dl>

      <div className="mt-4">
        <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
          {t("uxOperations.runtimeAccess", "Runtime access")}
        </h4>
        {entitlements.length === 0 ? (
          <p className="mt-1 text-xs text-muted">{t("uxOperations.noEntitlements", "No Entitlements recorded.")}</p>
        ) : (
          <ul className="mt-2 divide-y divide-line rounded-md border border-line bg-canvas" aria-label={t("uxOperations.entitlementList", "Entitlements") }>
            {entitlements.map((entitlement) => (
              <li className="grid gap-1 px-3 py-2 text-xs sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-3" key={entitlement.key}>
                <span className="min-w-0 break-all font-mono text-ink">{entitlement.key}</span>
                <span className="text-muted sm:text-right">
                  {t("uxOperations.entitlementTypedValue", "{{type}}: {{value}}", {
                    type: entitlement.type,
                    value: displayEntitlementValue(entitlement, t("uxOperations.allowed", "Allowed"), t("uxOperations.denied", "Not allowed")),
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

const sensitiveFields = new Set([
  "activation_secret",
  "access_token",
  "enrollment_token",
  "fingerprint_hash",
  "id_token",
  "lookup_hash",
  "password_hash",
  "product_key",
  "refresh_token",
  "secret_hash",
  "signed_license_token",
  "token",
  "totp_encrypted",
]);

function redactValue(key: string, value: unknown): unknown {
  if (sensitiveFields.has(key.toLowerCase())) return "[redacted]";
  if (Array.isArray(value)) return value.map((item) => redactValue("", item));
  if (value !== null && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([child, item]) => [child, redactValue(child, item)]));
  return value;
}

function safeValue(key: string, value: unknown, notSet: string) {
  const safe = redactValue(key, value);
  if (safe === null || safe === undefined) return notSet;
  return typeof safe === "object" ? JSON.stringify(safe) : String(safe);
}

// changedAuditFields lists keys whose redacted values differ between before and after.
function changedAuditFields(before?: Record<string, unknown>, after?: Record<string, unknown>) {
  return [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].filter((key) => JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key]));
}

// AuditChangeTable is the always-visible field/before/after view used inside
// an expanded Audit Log row. It applies the same redaction as AuditChangeEvidence.
export function AuditChangeTable({ before, after }: { before?: Record<string, unknown>; after?: Record<string, unknown> }) {
  const { t } = useTranslation();
  const fields = changedAuditFields(before, after);
  if (!fields.length) return <p className="text-xs text-muted">{t("audit.noChanges", "No field changes recorded")}</p>;
  const notSet = t("uxOperations.notSet", "Not set");
  return (
    <table className="w-full table-fixed text-xs">
      <thead className="text-[10px] uppercase tracking-[0.08em] text-muted">
        <tr className="border-b border-line">
          <th className="w-2/5 py-1.5 pr-3 text-left font-normal">{t("audit.field", "Field")}</th>
          <th className="py-1.5 pr-3 text-left font-normal">{t("uxOperations.before", "Before")}</th>
          <th className="py-1.5 text-left font-normal">{t("uxOperations.after", "After")}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-line font-mono">
        {fields.map((field) => (
          <tr key={field}>
            <td className="break-all py-1.5 pr-3 text-muted">{field}</td>
            <td className="break-all py-1.5 pr-3 text-danger line-through decoration-danger/50">{safeValue(field, before?.[field], notSet)}</td>
            <td className="break-all py-1.5 text-success">{safeValue(field, after?.[field], notSet)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AuditChangeEvidence({ before, after }: { before?: Record<string, unknown>; after?: Record<string, unknown> }) {
  const { t } = useTranslation();
  const fields = changedAuditFields(before, after);
  if (!fields.length) return null;

  return (
    <details className="mt-2 text-xs">
      <summary className="cursor-pointer text-muted">{t("uxOperations.changeEvidence", "What changed")}</summary>
      <dl className="mt-2 divide-y divide-line rounded-md border border-line bg-canvas">
        {fields.map((field) => (
          <div className="grid gap-1 px-3 py-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] sm:gap-3" key={field}>
            <dt className="break-all font-mono text-muted">{field}</dt>
            <dd className="break-all text-muted"><span className="mr-1">{t("uxOperations.before", "Before")}:</span>{safeValue(field, before?.[field], t("uxOperations.notSet", "Not set"))}</dd>
            <dd className="break-all text-ink"><span className="mr-1">{t("uxOperations.after", "After")}:</span>{safeValue(field, after?.[field], t("uxOperations.notSet", "Not set"))}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
